import React, { useCallback } from 'react'

import { useDispatch, useSelector } from 'react-redux'

import type { MenuPreferences } from '@library/api-menu-experience'
import type { MenuRecipeTileRequestParams, Recipe, RecipeUUID } from '@library/menu-service'

import { useCurrentCollectionId } from 'hooks/menu-collections'
import { useMenuMeta } from 'hooks/menu-metadata/useMenuMeta'
import { useStaticArray } from 'hooks/useStaticArray'
import { MENU_RECIPE_LIST_SKIP_TARGET_ID } from 'routes/Menu/accessibility'
import { selectRecipeVariant, showDetailRecipe } from 'routes/Menu/actions/menuRecipeDetails'
import { CaloriesTutorial } from 'routes/Menu/components/MenuPreferences'
import { useBasket } from 'routes/Menu/domains/basket'
import { useCollectionRecipes, useSetBrowserCTAVisibility } from 'routes/Menu/domains/menu'
import { useFlexRecipesWeb } from 'routes/Menu/hooks/useFlexRecipesWeb'
import { getIsAuthenticated } from 'selectors/auth'

import { CTAToAllRecipes } from '../CTAToAllRecipes'
import { RecipeTile } from './RecipeTileWrapper'
import { useCustomisationEducation } from './useCustomisationEducation'
import { useTrackRecipeListEffect } from './useTrackRecipeListEffect'
import { useTrackSoldOutRecipesEffect } from './useTrackSoldOutRecipesEffect'

import css from './RecipeList.css'

export type RefreshedRecipesById = Partial<Record<RecipeUUID, Recipe>>

export type RefreshRecipeTile = (
  recipeId: RecipeUUID,
  numPortions: MenuRecipeTileRequestParams['numPortions'],
) => Promise<void>

export type RecipeListProps = {
  menuPreferences?: MenuPreferences
  refreshedRecipesById?: RefreshedRecipesById
  refreshRecipeTile?: RefreshRecipeTile
}

export const RecipeList = ({
  menuPreferences,
  refreshedRecipesById,
  refreshRecipeTile,
}: RecipeListProps) => {
  const currentCollectionId = useCurrentCollectionId()
  const isFlexRecipesWebEnabled = useFlexRecipesWeb()
  const isAuthenticated = useSelector(getIsAuthenticated)
  const { menuId } = useMenuMeta()

  const recipeSets = useCollectionRecipes(currentCollectionId)

  const immutableRecipes = useStaticArray(recipeSets.map(({ recipe }) => recipe))
  useTrackSoldOutRecipesEffect(immutableRecipes)
  useTrackRecipeListEffect(currentCollectionId, immutableRecipes)

  const dispatch = useDispatch()

  const {
    canAddRecipes,
    addRecipeByUuid,
    getQuantitiesForRecipeId,
    removeRecipeByUuid,
    reachedLimit,
  } = useBasket()

  const { setBrowserCTAVisible } = useSetBrowserCTAVisibility()

  const onClick = useCallback(
    (recipeUuid: RecipeUUID) => dispatch(showDetailRecipe(recipeUuid)),
    [dispatch],
  )
  const selectVariant = useCallback(
    ({
      variant,
      variantOutOfStock,
      originalRecipeId,
      recipeReference,
    }: {
      variant: Recipe
      variantOutOfStock: boolean
      originalRecipeId: RecipeUUID
      recipeReference: string
    }) => {
      dispatch(
        selectRecipeVariant({
          originalRecipeId,
          variant,
          collectionId: currentCollectionId,
          variantOutOfStock,
          view: 'grid',
          close: true,
          recipeReference,
        }),
      )
    },
    [currentCollectionId, dispatch],
  )

  const { recipeIdForEducation, closeEducation } = useCustomisationEducation(recipeSets)

  return (
    <div className={css.recipeListWrapper}>
      <div className={css.threeTileRecipeList} id={MENU_RECIPE_LIST_SKIP_TARGET_ID} tabIndex={-1}>
        {recipeSets.map((value, index: number) => {
          const renderedRecipe = refreshedRecipesById?.[value.recipe.id] ?? value.recipe

          return (
            <React.Fragment key={value.reference || value.recipe.id || `recipe-${index}`}>
              {index === 5 && <CaloriesTutorial />}
              <RecipeTile
                recipe={renderedRecipe}
                currentCollectionId={currentCollectionId}
                menuId={menuId}
                onClick={onClick}
                menuPreferences={menuPreferences}
                showCustomisedTileFooter={isAuthenticated}
                showFlexPortionTileBadge={isFlexRecipesWebEnabled}
                setBrowserCTAVisible={setBrowserCTAVisible}
                canAddRecipes={canAddRecipes}
                addRecipe={addRecipeByUuid}
                removeRecipe={removeRecipeByUuid}
                reachedLimit={reachedLimit}
                selectVariant={selectVariant}
                recipeReference={value.reference}
                originalId={value.originalId}
                index={index + 1}
                quantity={getQuantitiesForRecipeId(value.recipe.id)}
                showCustomisationEducation={value.recipe.id === recipeIdForEducation}
                closeCustomisationEducation={closeEducation}
                refreshRecipeTile={refreshRecipeTile}
              />
            </React.Fragment>
          )
        })}
        <CTAToAllRecipes />
      </div>
    </div>
  )
}
